import { Raydium } from "@raydium-io/raydium-sdk-v2";
import { getLaunchlabCluster } from "@/lib/idx-launchlab/constants";
import { LAUNCHLAB_SYMBOL_TOO_LONG_MESSAGE } from "@/lib/idx-launchlab/token-field-limits";
import {
  formatInsufficientSolLaunchError,
  isInsufficientSolLaunchError,
} from "@/lib/idx-launchlab/launch-balance";
import {
  isPhantomBlockedError,
  isWalletNotConnectedError,
  WALLET_NOT_CONNECTED_MESSAGE,
} from "@/utils/walletTransactionHelpers";

/** @type {{ at: number; configs: unknown[] } | null} */
let clientLaunchConfigsCache = null;
const CLIENT_CONFIGS_TTL_MS = 30 * 60 * 1000;

async function fetchLaunchConfigsViaAppApi() {
  const now = Date.now();
  if (
    clientLaunchConfigsCache?.configs?.length &&
    now - clientLaunchConfigsCache.at < CLIENT_CONFIGS_TTL_MS
  ) {
    return clientLaunchConfigsCache.configs;
  }

  const cluster = getLaunchlabCluster();
  const res = await fetch(`/api/idx-launchlab/launch-configs?cluster=${encodeURIComponent(cluster)}`, {
    cache: "no-store",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !Array.isArray(json.configs) || !json.configs.length) {
    throw new Error(
      typeof json.error === "string"
        ? json.error
        : "Could not load launch settings. Wait a minute and try Launch coin again.",
    );
  }

  clientLaunchConfigsCache = { at: now, configs: json.configs };
  return json.configs;
}

/** Route Raydium SDK launch config fetch through our cached API (avoids browser 429s). */
function patchLaunchConfigsFetch(raydium) {
  raydium.api.fetchLaunchConfigs = fetchLaunchConfigsViaAppApi;
}

/** Prefetch launch configs (e.g. on review step) so Launch coin is faster. */
export function prefetchLaunchConfigs() {
  void fetchLaunchConfigsViaAppApi().catch(() => {});
}

/**
 * @param {unknown} err
 * @param {{ initialBuySol?: string | number | null; hasFirstBuy?: boolean }} [context]
 * @returns {string}
 */
export function formatLaunchCreateError(err, context = {}) {
  if (isInsufficientSolLaunchError(err)) {
    return formatInsufficientSolLaunchError(err, context.initialBuySol);
  }
  if (isWalletNotConnectedError(err)) {
    return WALLET_NOT_CONNECTED_MESSAGE;
  }

  const message = err instanceof Error ? err.message?.trim() : "";
  if (message) {
    const lower = message.toLowerCase();
    // Post-create failures already state the coin exists — never rewrite them.
    if (lower.startsWith("coin created")) {
      return message;
    }
    if (
      lower.includes("launch service is busy") ||
      lower.includes("too many requests") ||
      lower.includes("could not load launch") ||
      lower.includes("launch settings")
    ) {
      return message;
    }
    if (lower.includes("network error") || lower.includes("429")) {
      return "Launch settings could not be loaded (network busy). Wait about a minute and press Launch coin again.";
    }
    if (
      lower.includes("blockhash not found") ||
      lower.includes("block height exceeded") ||
      lower.includes("expired blockhash")
    ) {
      return "Launch transaction expired before it could be sent. Press Launch coin again.";
    }
    if (isPhantomBlockedError(err)) {
      if (context.hasFirstBuy) {
        return (
          "Your wallet blocked the launch. Nothing was launched. " +
          "Try IDX Wallet, or launch without a first buy and buy on your coin page after."
        );
      }
      return "Your wallet blocked the launch request. Press Launch coin again.";
    }
    if (lower.includes("symbol length") || lower.includes("shorter than 11")) {
      return LAUNCHLAB_SYMBOL_TOO_LONG_MESSAGE;
    }
  }
  return message || "Launch failed. Try again in a minute.";
}

/**
 * Load Raydium SDK with wallet signer (same pattern as liquidity-pool-creator).
 * @param {{ connection: import("@solana/web3.js").Connection; owner: import("@solana/web3.js").PublicKey; signAllTransactions?: (txs: import("@solana/web3.js").Transaction[]) => Promise<import("@solana/web3.js").Transaction[]>; signTransaction?: (tx: import("@solana/web3.js").Transaction) => Promise<import("@solana/web3.js").Transaction> }} params
 */
export async function loadRaydium({ connection, owner, signAllTransactions, signTransaction }) {
  const signAll =
    signAllTransactions ??
    (async (txs) => {
      if (!signTransaction) throw new Error("Wallet cannot sign transactions.");
      const out = [];
      for (const tx of txs) {
        out.push(await signTransaction(tx));
      }
      return out;
    });

  const raydium = await Raydium.load({
    connection,
    owner,
    signAllTransactions: signAll,
    cluster: getLaunchlabCluster(),
    disableFeatureCheck: true,
    blockhashCommitment: "finalized",
  });

  if (!raydium.launchpad) {
    throw new Error("Raydium SDK launchpad module not loaded.");
  }

  patchLaunchConfigsFetch(raydium);

  return raydium;
}

/**
 * Raydium `execute()` returns `{ txId }` for a single tx or `{ txIds }` for multi-tx builds.
 * @param {unknown} executed
 * @returns {string | null}
 */
export function firstRaydiumExecuteTxId(executed) {
  if (executed == null) return null;
  if (typeof executed === "string") {
    const trimmed = executed.trim();
    return trimmed || null;
  }
  if (typeof executed !== "object") return null;

  const record = /** @type {{ txId?: unknown; txIds?: unknown }} */ (executed);
  if (typeof record.txId === "string") {
    const trimmed = record.txId.trim();
    if (trimmed) return trimmed;
  }
  if (Array.isArray(record.txIds) && record.txIds.length) {
    const first = record.txIds[0];
    if (typeof first === "string" && first.trim()) return first.trim();
  }
  return null;
}

/** @param {unknown} executed @returns {string[]} */
export function raydiumExecuteTxIds(executed) {
  if (executed == null) return [];
  if (typeof executed === "string") {
    const trimmed = executed.trim();
    return trimmed ? [trimmed] : [];
  }
  if (typeof executed === "object") {
    const record = /** @type {{ txIds?: unknown; signedTxs?: unknown }} */ (executed);
    if (Array.isArray(record.txIds) && record.txIds.length) {
      return record.txIds.filter(
        (id) => typeof id === "string" && id.trim() && id.trim() !== "tx skipped",
      );
    }
  }
  const single = firstRaydiumExecuteTxId(executed);
  return single ? [single] : [];
}

/** @param {unknown} executed */
export function summarizeExecutedForDebug(executed) {
  if (executed == null) return { present: false };
  if (typeof executed !== "object") {
    return { present: true, type: typeof executed, txIds: raydiumExecuteTxIds(executed) };
  }
  const record = /** @type {Record<string, unknown>} */ (executed);
  return {
    present: true,
    keys: Object.keys(record).slice(0, 16),
    txIds: raydiumExecuteTxIds(executed),
    signedTxCount: Array.isArray(record.signedTxs) ? record.signedTxs.length : null,
  };
}
